import { ChevronRight } from '@mui/icons-material';
import { NilStatuses, type RosterItemResponse } from '@on3/api';
import { On3IconLock } from '@on3/icons';
import { Avatar } from '@on3/ui-lib/components/Avatar';
import { Link } from '@on3/ui-lib/components/Link';
import { adjustColorForReadability } from '@on3/ui-lib/utils/accessibility/color';
import { formatCurrency } from '@on3/ui-lib/utils/formatters';
import { VerifiedBadge } from 'components/Elite/VerifiedBadge/VerifiedBadge';
import { StarRating } from 'components/UI/StarRating/StarRating';

import { HighSchoolItemStatus } from './HighSchoolItemStatus';
import styles from './HighSchoolRosterItem.module.scss';

interface IHighSchoolRosterItemProps extends RosterItemResponse {
  sport: string;
}
export const HighSchoolRosterItem = ({
  player,
  rating,
  organization,
  nilValue,
  status,
  rpm,
  sport: rosterSport,
}: IHighSchoolRosterItemProps) => {
  const {
    fullName,
    slug,
    hometown,
    defaultAsset,
    position,
    athleteVerified,
    jerseyNumber,
    height,
    weight,
    recruitmentKey,
    classYear,
  } = player || {};
  const {
    fiveStarPlus,
    consensusPositionRank,
    consensusNationalRank,
    consensusStateRank,
    consensusRating,
    positionAbbr,
    stateAbbr,
    consensusStars,
  } = rating || {};
  const sport = rosterSport.toLowerCase();
  const recruitHref = (slug && `/rivals/${slug}/industry-comparison/`) || '';
  const primaryColor = adjustColorForReadability(
    organization?.primaryColor || '#fe3b1f',
  );
  const totalNilValue = nilValue?.totalValue ?? 0;

  const showNilValue =
    (nilValue?.nilStatus === NilStatuses.Normal ||
      nilValue?.nilStatus === NilStatuses.Show ||
      nilValue?.nilStatus === NilStatuses.Final) &&
    totalNilValue >= 200000;

  return (
    <div className={styles.playerRankingItem}>
      <span className={styles.jerseyNumber}>
        <span>{jerseyNumber ?? '-'}</span>
      </span>

      <figure className={styles.headshot}>
        <Avatar
          altText={fullName}
          domain={defaultAsset?.domain}
          source={defaultAsset?.source}
        />
      </figure>

      <div className={styles.details}>
        <Link href={`/rivals/${slug}/`} style={{ color: primaryColor }}>
          {fullName}
        </Link>
        <div className={styles.vitals}>
          {!!classYear && <span>{classYear}</span>}
          {!!height && <span>{height}</span>}
          {!!weight && <span>{weight}</span>}
        </div>
        <div>{hometown?.abbr && <span>{hometown?.abbr}</span>}</div>
      </div>

      <span className={styles.position}>{position?.abbr ?? '-'}</span>

      <StarRating
        className={styles.starRating}
        fiveStarPlus={fiveStarPlus}
        hasBorder={false}
        href={recruitHref}
        isBoldRating={false}
        isConsensus
        nationalRank={consensusNationalRank}
        position={positionAbbr || position?.abbr}
        positionRank={consensusPositionRank}
        primaryColor={primaryColor}
        rating={consensusRating}
        small
        sport={rating?.sport?.name}
        stars={consensusStars}
        state={stateAbbr}
        stateRank={consensusStateRank}
        year={`${rating?.year}`}
      />
      <div className={styles.nilContainer}>
        <span className={styles.nilLabel}>NIL Value:</span>

        <span className={styles.nilValuation}>
          {showNilValue ? (
            formatCurrency(totalNilValue)
          ) : (
            <On3IconLock fill={primaryColor} />
          )}
        </span>
      </div>

      <HighSchoolItemStatus
        className={styles.status}
        predictions={rpm}
        recruitmentKey={recruitmentKey}
        slug={slug}
        sport={sport}
        status={status}
        year={`${classYear}`}
      />
      {/* TODO: NEED TIER ADDED TO PLAYER RESPONSE */}
      <div className={styles.claimStatus}>
        {athleteVerified ? (
          <VerifiedBadge />
        ) : (
          <Link
            className={styles.claim}
            forceReload
            href={`/rivals/${player?.slug}/claim-profile/`}
            style={{
              color: primaryColor,
            }}
          >
            Claim Profile <ChevronRight />
          </Link>
        )}
      </div>
    </div>
  );
};
